/**
 * Case History Storage Service
 * Handles localStorage operations for case history persistence
 */

const STORAGE_KEY_PREFIX = 'caseHistory_';
const MAX_CASES_PER_PLAN = 50;

/**
 * Generate a unique key from first name and last name
 * @param {string} firstName - Patient's first name
 * @param {string} lastName - Patient's last name
 * @returns {string} Normalized key in format "firstname_lastname"
 */
export const generateCaseKey = (firstName, lastName) => {
  const normalizedFirst = (firstName || '').trim().toLowerCase().replace(/\s+/g, '');
  const normalizedLast = (lastName || '').trim().toLowerCase().replace(/\s+/g, '');

  if (!normalizedFirst && !normalizedLast) {
    return `unknown_${Date.now()}`;
  }

  return `${normalizedFirst}_${normalizedLast}`;
};

/**
 * Get the localStorage key for a specific plan and org context
 * @param {string} planName - The plan name
 * @param {Object} orgContext - Organization context
 * @param {string} orgContext.orgGroupId - Organization group ID
 * @param {string} orgContext.organizationId - Organization ID
 * @returns {string} localStorage key
 */
const getStorageKey = (planName, orgContext) => {
  const { orgGroupId, organizationId } = orgContext;
  return `${STORAGE_KEY_PREFIX}${planName}_${orgGroupId}_${organizationId}`;
};

/**
 * Retrieve all cases for a specific plan from localStorage
 * @param {string} planName - The plan name to get cases for
 * @param {Object} [orgContext={}] - Organization context
 * @returns {Array} Array of case records, sorted by savedAt (newest first)
 */
export const getCasesForPlan = (planName, orgContext) => {
  if (!planName) return [];

  try {
    const storageKey = getStorageKey(planName, orgContext);
    const stored = localStorage.getItem(storageKey);

    if (!stored) return [];

    const cases = JSON.parse(stored);

    // Validate that it's an array
    if (!Array.isArray(cases)) return [];

    // Sort by savedAt descending (newest first)
    return cases.sort((a, b) => {
      const dateA = new Date(a.savedAt || 0);
      const dateB = new Date(b.savedAt || 0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error reading case history from localStorage:', error);
    return [];
  }
};

/**
 * Save or update a case in history
 * If a case with the same key (firstName + lastName) exists, it will be updated
 * @param {string} planName - The plan name
 * @param {object} formData - The form data to save
 * @param {object} customFieldValues - Plan-specific custom field values
 * @param {boolean} wasTriggered - Whether the case was triggered (vs just saved)
 * @param {string} caseId - The case ID returned from the API (optional)
 * @param {Object} [orgContext={}] - Organization context
 * @returns {object} The saved case record
 */
export const saveCaseToHistory = (planName, formData, customFieldValues = {}, wasTriggered = false, caseId = null, orgContext) => {
  if (!planName) {
    throw new Error('Plan name is required to save case history');
  }

  const firstName = formData.fname || formData.firstName || '';
  const lastName = formData.lname || formData.lastName || '';
  const caseKey = generateCaseKey(firstName, lastName);
  const now = new Date().toISOString();

  // Get existing cases
  const existingCases = getCasesForPlan(planName, orgContext);

  // Check if case with same key exists
  const existingIndex = existingCases.findIndex((c) => c.key === caseKey);

  // Create the case record
  const caseRecord = {
    key: caseKey,
    firstName: firstName || 'Unknown',
    lastName: lastName || 'Patient',
    formData: { ...formData },
    customFieldValues: { ...customFieldValues },
    savedAt: now,
    triggeredAt: wasTriggered ? now : (existingIndex >= 0 ? existingCases[existingIndex].triggeredAt : null),
    wasTriggered: wasTriggered || (existingIndex >= 0 ? existingCases[existingIndex].wasTriggered : false),
    caseId: caseId || (existingIndex >= 0 ? existingCases[existingIndex].caseId : null),
  };

  let updatedCases;

  if (existingIndex >= 0) {
    // Update existing case
    updatedCases = [...existingCases];
    updatedCases[existingIndex] = caseRecord;
  } else {
    // Add new case
    updatedCases = [caseRecord, ...existingCases];
  }

  // Enforce max cases limit (remove oldest if over limit)
  if (updatedCases.length > MAX_CASES_PER_PLAN) {
    // Sort by savedAt to ensure we remove the oldest
    updatedCases.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    updatedCases = updatedCases.slice(0, MAX_CASES_PER_PLAN);
  }

  // Save to localStorage
  try {
    const storageKey = getStorageKey(planName, orgContext);
    localStorage.setItem(storageKey, JSON.stringify(updatedCases));
  } catch (error) {
    console.error('Error saving case history to localStorage:', error);
    throw new Error('Failed to save case to history');
  }

  return caseRecord;
};

/**
 * Delete a single case from history
 * @param {string} planName - The plan name
 * @param {string} caseKey - The case key to delete
 * @param {Object} [orgContext={}] - Organization context
 * @returns {boolean} True if deleted, false if not found
 */
export const deleteCaseFromHistory = (planName, caseKey, orgContext) => {
  if (!planName || !caseKey) return false;

  try {
    const existingCases = getCasesForPlan(planName, orgContext);
    const filteredCases = existingCases.filter((c) => c.key !== caseKey);

    if (filteredCases.length === existingCases.length) {
      return false; // Case not found
    }

    const storageKey = getStorageKey(planName, orgContext);
    localStorage.setItem(storageKey, JSON.stringify(filteredCases));

    return true;
  } catch (error) {
    console.error('Error deleting case from history:', error);
    return false;
  }
};

/**
 * Delete multiple cases from history
 * @param {string} planName - The plan name
 * @param {Array<string>} caseKeys - Array of case keys to delete
 * @param {Object} [orgContext={}] - Organization context
 * @returns {number} Number of cases deleted
 */
export const deleteMultipleCases = (planName, caseKeys, orgContext) => {
  if (!planName || !caseKeys || caseKeys.length === 0) return 0;

  try {
    const existingCases = getCasesForPlan(planName, orgContext);
    const keySet = new Set(caseKeys);
    const filteredCases = existingCases.filter((c) => !keySet.has(c.key));

    const deletedCount = existingCases.length - filteredCases.length;

    const storageKey = getStorageKey(planName, orgContext);
    localStorage.setItem(storageKey, JSON.stringify(filteredCases));

    return deletedCount;
  } catch (error) {
    console.error('Error deleting cases from history:', error);
    return 0;
  }
};

/**
 * Clear all history for a specific plan
 * @param {string} planName - The plan name
 * @param {Object} [orgContext={}] - Organization context
 * @returns {boolean} True if cleared successfully
 */
export const clearHistoryForPlan = (planName, orgContext) => {
  if (!planName) return false;

  try {
    const storageKey = getStorageKey(planName, orgContext);
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Error clearing history for plan:', error);
    return false;
  }
};

/**
 * Get a single case by key
 * @param {string} planName - The plan name
 * @param {string} caseKey - The case key to retrieve
 * @param {Object} [orgContext={}] - Organization context
 * @returns {object|null} The case record or null if not found
 */
export const getCaseByKey = (planName, caseKey, orgContext) => {
  if (!planName || !caseKey) return null;

  const cases = getCasesForPlan(planName, orgContext);
  return cases.find((c) => c.key === caseKey) || null;
};

/**
 * Check if a case exists for a given patient name
 * @param {string} planName - The plan name
 * @param {string} firstName - Patient's first name
 * @param {string} lastName - Patient's last name
 * @param {Object} [orgContext={}] - Organization context
 * @returns {boolean} True if case exists
 */
export const caseExists = (planName, firstName, lastName, orgContext) => {
  const caseKey = generateCaseKey(firstName, lastName);
  return getCaseByKey(planName, caseKey, orgContext) !== null;
};

/**
 * Get the count of cases for a plan
 * @param {string} planName - The plan name
 * @param {Object} [orgContext={}] - Organization context
 * @returns {number} Number of saved cases
 */
export const getCaseCount = (planName, orgContext) => {
  return getCasesForPlan(planName, orgContext).length;
};

/**
 * Update the triggeredAt timestamp for a case
 * @param {string} planName - The plan name
 * @param {string} caseKey - The case key to update
 * @param {string} caseId - The case ID returned from the API (optional)
 * @param {Object} [orgContext={}] - Organization context
 * @returns {object|null} Updated case record or null if not found
 */
export const markCaseAsTriggered = (planName, caseKey, caseId = null, orgContext) => {
  if (!planName || !caseKey) return null;

  try {
    const existingCases = getCasesForPlan(planName, orgContext);
    const caseIndex = existingCases.findIndex((c) => c.key === caseKey);

    if (caseIndex === -1) return null;

    const now = new Date().toISOString();
    const updatedCase = {
      ...existingCases[caseIndex],
      triggeredAt: now,
      savedAt: now, // Also update savedAt to move to top
      wasTriggered: true,
      caseId: caseId || existingCases[caseIndex].caseId, // Update caseId if provided
    };

    existingCases[caseIndex] = updatedCase;

    const storageKey = getStorageKey(planName, orgContext);
    localStorage.setItem(storageKey, JSON.stringify(existingCases));

    return updatedCase;
  } catch (error) {
    console.error('Error marking case as triggered:', error);
    return null;
  }
};

export default {
  generateCaseKey,
  getCasesForPlan,
  saveCaseToHistory,
  deleteCaseFromHistory,
  deleteMultipleCases,
  clearHistoryForPlan,
  getCaseByKey,
  caseExists,
  getCaseCount,
  markCaseAsTriggered,
};
